// Endgame Progress Panel (F04)
// Shows the player where they stand vs each of the three ending gates.
// Gated by Day >= 7 so the first week stays exploratory.

import { variableManager } from '../engine/variables.js';
import { L } from '../systems/localization.js';

// L.get() returns the key itself when missing; this wrapper falls back to a
// provided default so early-day test runs (pre-L10n-load) still render.
function t(key, fallback) {
    const v = L.get(key);
    return v === key ? fallback : v;
}

class EndgameProgress {
    constructor() {
        this._el = null;
    }

    // Called from the pause-menu button handler. Returns false if too early.
    open() {
        const day = variableManager.getValue('Day') || 1;
        if (day < 7) return false;
        this._ensureDom();
        this._render();
        this._el.classList.remove('hidden');
        return true;
    }

    close() {
        if (this._el) this._el.classList.add('hidden');
    }

    _ensureDom() {
        if (this._el) return;
        const el = document.createElement('div');
        el.id = 'overlay-endgame-progress';
        el.className = 'overlay overlay-endgame-progress hidden';
        el.innerHTML = `
            <div class="overlay-box eg-box">
                <div class="overlay-header">
                    <h2>${t('endgame.progress.title', 'Endgame Progress')}</h2>
                    <button class="btn-overlay-close" aria-label="Close">&times;</button>
                </div>
                <div class="eg-body"></div>
                <div class="eg-footer">
                    <p class="eg-hint">${t('endgame.progress.hint', 'Endings unlock from Day 35 onward. Each path has its own requirements. You can only land one per playthrough.')}</p>
                </div>
            </div>
        `;
        el.querySelector('.btn-overlay-close').addEventListener('click', () => this.close());
        el.addEventListener('click', (e) => {
            if (e.target === el) this.close();
        });
        document.body.appendChild(el);
        this._el = el;
    }

    _calcFameScore(v) {
        // Mirrors Endgame.ink calc_fame_score() — if Ink changes, update here.
        let score = 0;
        if ((v.streams_completed || 0) >= 20) score++;
        if ((v.sissy_followers || 0) >= 200) score++;
        if ((v.stream_reputation || 0) >= 60) score++;
        if ((v.onlyfems_subscribers || 0) >= 100) score++;
        if ((v.stream_viral_count || 0) >= 2) score++;
        const collabs = (v.collab_lily_done || 0) + (v.collab_master_done || 0) + (v.collab_pink_done || 0);
        if (collabs >= 2) score++;
        if ((v.sissy_fame || 0) >= 60) score++;
        return score;
    }

    _read(name) {
        // Prefer story.variablesState (authoritative Ink state) over baseVar.
        // Ink-only vars like master_intensity / MasterRelationship / master_order_*
        // are not in OBSERVED_VARIABLES, so variableManager.getValue() returns 0
        // for them — caused the KEPT-path "Requires a Master" false-close bug
        // even on completed Owner runs (foundby:Player 2026-05-01).
        const story = variableManager._story;
        if (story) {
            try {
                const v = story.variablesState[name];
                if (v !== undefined && v !== null) return v;
            } catch (e) {}
        }
        return variableManager.getValue(name) || 0;
    }

    _render() {
        const v = {
            Day: this._read('Day'),
            Femininity: this._read('Femininity'),
            Submissiveness: this._read('Submissiveness'),
            MasterRelationship: this._read('MasterRelationship'),
            Money: this._read('Money'),
            master_intensity: this._read('master_intensity'),
            master_order_chastity: this._read('master_order_chastity'),
            master_order_plug: this._read('master_order_plug'),
            sissy_followers: this._read('sissy_followers'),
            stream_reputation: this._read('stream_reputation'),
            streams_completed: this._read('streams_completed'),
            onlyfems_subscribers: this._read('onlyfems_subscribers'),
            stream_viral_count: this._read('stream_viral_count'),
            sissy_fame: this._read('sissy_fame'),
            collab_lily_done: this._read('collab_lily_done'),
            collab_master_done: this._read('collab_master_done'),
            collab_pink_done: this._read('collab_pink_done'),
            // v0.2.6 additions
            doxxing_arc_stage: this._read('doxxing_arc_stage'),
            doxxing_arc_resolution: this._read('doxxing_arc_resolution'),
            independent_arc_1_seen: this._read('independent_arc_1_seen'),
            independent_arc_2_seen: this._read('independent_arc_2_seen'),
            independent_arc_3_seen: this._read('independent_arc_3_seen'),
            chastity_days_worn: this._read('chastity_days_worn'),
            permanent_lock: this._read('permanent_lock'),
            permanent_lock_day: this._read('permanent_lock_day'),
            chastity_milestone_d90: this._read('chastity_milestone_d90'),
            chastity_milestone_d120: this._read('chastity_milestone_d120'),
            masc_path: this._read('masc_path'),
        };

        const fameScore = this._calcFameScore(v);

        // Kept requires MR >= 8. Per v0.2.5.8.1 route caps (Roommate 4, Landlord 7,
        // Owner 10) only Owner can reach it. Pre-fix the panel told Landlord
        // players the path was open and silently let them grind toward an
        // unreachable threshold.
        const e1Reqs = [
            { label: t('endgame.req.day', 'Day'), cur: v.Day, req: 35 },
            { label: t('endgame.req.master_rel', 'Master Relationship'), cur: v.MasterRelationship, req: 8 },
            { label: t('endgame.req.femininity', 'Femininity'), cur: v.Femininity, req: 80 },
            { label: t('endgame.req.submissiveness', 'Submissiveness'), cur: v.Submissiveness, req: 70 },
            { label: t('endgame.req.chastity_order', 'Chastity order completed'), cur: v.master_order_chastity, req: 3 },
            { label: t('endgame.req.plug_order', 'Plug order completed'), cur: v.master_order_plug, req: 3 },
        ];
        if (v.permanent_lock) {
            e1Reqs.push({ label: t('endgame.req.permanent_lock', 'Permanent Lock accepted'), cur: 1, req: 1, bonus: true });
            // Post-ceremony milestone tracker (bugfix#0, promised to bulbasquirtle):
            // days since the lock closed for good toward the next post-collar beat.
            // permanent_lock_day defaults to -1 until the first overnight after the
            // ceremony stamps it. MILESTONES MUST stay in sync with the overnight
            // gates in Sleep.ink (Day - permanent_lock_day >= N), change both.
            const MILESTONES = [30, 60];
            if (v.permanent_lock_day >= 0) {
                const daysSealed = Math.max(0, v.Day - v.permanent_lock_day);
                const next = MILESTONES.find(m => daysSealed < m);
                if (next !== undefined) {
                    e1Reqs.push({ label: t('endgame.req.days_sealed', 'Days sealed (next milestone)'), cur: daysSealed, req: next, bonus: true });
                } else {
                    e1Reqs.push({ label: t('endgame.req.days_sealed_max', 'Days sealed'), cur: daysSealed, req: daysSealed, bonus: true });
                }
            }
        }
        const e1 = {
            key: 'e1',
            name: t('endgame.e1.name', 'Kept'),
            tagline: t('endgame.e1.tagline', 'Owned. Collared. His.'),
            flavour: t('endgame.e1.flavour', 'A kept sissy ending. You wake in his bed, in his collar, in his rules. Worn surfaces — pillowcase, ring, leash — show in the final shot.'),
            closed: v.master_intensity < 3,
            closedReason: t('endgame.e1.closed', 'Requires Owner route. Landlord and Roommate caps cannot reach Master Relationship 8.'),
            reqs: e1Reqs,
        };

        const e2Locked = v.Submissiveness > 55 || v.MasterRelationship > 7;
        // v0.2.6: Independent route gets 3 milestone beats on Day 5/12/18.
        // These don't gate the ending but show the player they're on the
        // narrative track. Each is a discrete one-shot flag.
        const indyMilestonesSeen = (v.independent_arc_1_seen ? 1 : 0)
            + (v.independent_arc_2_seen ? 1 : 0)
            + (v.independent_arc_3_seen ? 1 : 0);
        const e2 = {
            key: 'e2',
            name: t('endgame.e2.name', 'Free'),
            tagline: t('endgame.e2.tagline', 'On my own terms.'),
            flavour: t('endgame.e2.flavour', 'You walk out of your last scene the way you walked in: chosen, not given. Money on the nightstand, your own clothes, your own keys.'),
            closed: e2Locked,
            closedReason: t('endgame.e2.closed', 'Sub or Master Relationship too high, this path is closed.'),
            reqs: [
                { label: 'Day', cur: v.Day, req: 35 },
                { label: 'Femininity', cur: v.Femininity, req: 60 },
                { label: 'Submissiveness (≤55)', cur: v.Submissiveness, req: 55, inverse: true },
                { label: 'Master Relationship (≤7)', cur: v.MasterRelationship, req: 7, inverse: true },
                { label: 'Money', cur: v.Money, req: 500 },
                { label: 'Solo milestones (Day 5/12/18)', cur: indyMilestonesSeen, req: 3, bonus: true },
            ],
        };

        // v0.2.6 Patreon Streaming Rework: doxxing arc with 4 resolution paths.
        // Surfaces the current stage so famous-path players see "where am I"
        // (hint at end-of-run, threat in flight, etc.) instead of silently
        // having the arc tick in the background.
        const doxxStageLabels = ['Inactive', 'Hint received', 'Threat received', 'Awaiting resolution', 'Resolved'];
        const doxxLabel = doxxStageLabels[v.doxxing_arc_stage] || 'Inactive';
        const e3Reqs = [
            { label: 'Day', cur: v.Day, req: 35 },
            { label: 'Femininity', cur: v.Femininity, req: 60 },
            { label: 'Followers', cur: v.sissy_followers, req: 200 },
            { label: 'Stream reputation', cur: v.stream_reputation, req: 60 },
            { label: 'Fame score', cur: fameScore, req: 4 },
        ];
        if (v.doxxing_arc_stage > 0) {
            e3Reqs.push({ label: `Doxxing arc: ${doxxLabel}`, cur: v.doxxing_arc_stage, req: 4, inverse: false, bonus: true });
        }
        const e3 = {
            key: 'e3',
            name: t('endgame.e3.name', 'Famous'),
            tagline: t('endgame.e3.tagline', 'Going live.'),
            flavour: t('endgame.e3.flavour', 'A ring light, a sold-out collab, a name people whisper. The final scene is your viewer-count ticker hitting a number you do not look away from.'),
            closed: false,
            reqs: e3Reqs,
        };

        // v0.2.8: Masculine 4th ending (His Own Man). Only surfaces for players
        // actually on the masc route, so a feminine run's end-panel keeps the
        // original three cards.
        const e4 = {
            key: 'e4',
            name: t('endgame.e4.name', 'His Own Man'),
            tagline: t('endgame.e4.tagline', 'On his own terms.'),
            flavour: t('endgame.e4.flavour', 'You held the line, or clawed it back. The last scene is yours: your own name, your own clothes, no cage, no collar, the man you decided to stay.'),
            closed: v.masc_path < 1,
            closedReason: t('endgame.e4.closed', 'Requires the Masculine route: choose it in character creation, or reclaim your edge at the apartment mirror (Femininity 15-80).'),
            reqs: [
                { label: t('endgame.req.day', 'Day'), cur: v.Day, req: 35 },
                { label: t('endgame.req.femininity_under', 'Femininity (under 30)'), cur: v.Femininity, req: 30, inverse: true },
                { label: t('endgame.req.masc_route', 'Masculine route active'), cur: v.masc_path, req: 1 },
            ],
        };
        const epList = (v.masc_path >= 1) ? [e1, e2, e3, e4] : [e1, e2, e3];
        const cards = epList.map(ep => this._card(ep)).join('');
        this._el.querySelector('.eg-body').innerHTML = cards;
    }

    _card(ep) {
        const rows = ep.reqs.map(r => {
            const ok = r.inverse ? r.cur <= r.req : r.cur >= r.req;
            const icon = ok ? '✓' : (r.bonus ? '·' : '○');
            const valClass = ok ? 'eg-val-ok' : (r.bonus ? 'eg-val-bonus' : 'eg-val-pending');
            // Bonus rows are informational (Doxxing arc state, Independent
            // milestones, Permanent Lock) — they enrich the path but don't
            // gate the ending. Marked with a side-dot instead of a circle.
            return `<div class="eg-row${r.bonus ? ' eg-row-bonus' : ''}">
                <span class="eg-row-icon ${ok ? 'eg-icon-ok' : ''}">${icon}</span>
                <span class="eg-row-label">${r.label}</span>
                <span class="eg-row-val ${valClass}">${r.cur} / ${r.req}</span>
            </div>`;
        }).join('');
        // Bonus rows don't count toward path progress — only required reqs do.
        const required = ep.reqs.filter(r => !r.bonus);
        const metCount = required.filter(r => r.inverse ? r.cur <= r.req : r.cur >= r.req).length;
        const total = required.length;
        const pct = Math.round((metCount / total) * 100);
        // Audit D48: each path gets path-imagery copy in addition to the numbers.
        // The flavour line below the tagline tells the player what this path
        // FEELS like, not just what it requires. Reads as story, not spreadsheet.
        const flavour = ep.flavour ? `<div class="eg-card-flavour">${ep.flavour}</div>` : '';
        return `<div class="eg-card eg-card-${ep.key} ${ep.closed ? 'eg-card-closed' : ''}" data-path="${ep.key}">
            <div class="eg-card-head">
                <h3 class="eg-card-name">${ep.name}</h3>
                <span class="eg-card-progress">${metCount}/${total}</span>
            </div>
            <div class="eg-card-tagline">${ep.tagline}</div>
            ${flavour}
            ${ep.closed ? `<div class="eg-card-closed-note">✕ ${ep.closedReason}</div>` : ''}
            <div class="eg-card-bar"><div class="eg-card-bar-fill" style="width:${pct}%"></div></div>
            <div class="eg-card-rows">${rows}</div>
        </div>`;
    }
}

export const endgameProgress = new EndgameProgress();
if (typeof window !== 'undefined') {
    window._endgameProgress = endgameProgress;
}
